import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { ACCEPT, checkFile, uploadFile } from "@/lib/upload13";

const PROFILE = ["name", "species", "breed", "birthday", "weight", "color", "microchip", "registry", "license_number", "license_expiry", "vet_clinic", "vet_phone", "quirks"];

function useForm(init) {
  const [f, setF] = useState(init);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const inp = (k, l, t = "text", ph = "") => <Field label={l}><input type={t} className="q-input min-h-[44px] w-full" value={f[k] ?? ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`pet-form-${k}`} /></Field>;
  return [f, set, inp];
}

async function run(p, done, msg = "Saved") {
  try { const { data } = await p; toast.success(msg); done(data); } catch (e) { toast.error(errMsg(e)); }
}

export function PetDialog({ base, species, pet, onClose, onSaved }) {
  const [f, set, inp] = useForm(Object.fromEntries(PROFILE.map((k) => [k, pet?.[k] ?? (k === "species" ? "Dog" : "")])));
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const photo_upload_id = photo ? await uploadFile(base, photo).catch(() => null) : null;
    const body = { ...f, birthday: f.birthday || null, license_expiry: f.license_expiry || null, photo_upload_id };
    await run(pet ? api.put(`${base}/${pet.id}`, body) : api.post(base, body), onSaved);
    setBusy(false);
  };
  return (
    <FormDialog open onClose={onClose} title={pet ? `Edit ${pet.name}` : "Add a pet"} testid="pet-dialog">
      <div className="space-y-3">
        {inp("name", "Name", "text", "Biscuit")}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Species"><select className="q-input min-h-[44px] w-full" value={f.species} onChange={(e) => set("species", e.target.value)} data-testid="pet-form-species-select">{species.map((s) => <option key={s}>{s}</option>)}</select></Field>
          {inp("breed", "Breed")}{inp("birthday", "Birthday", "date")}{inp("weight", "Weight", "text", "68 lb")}{inp("color", "Color")}
          {inp("microchip", "Microchip number")}{inp("registry", "Microchip registry")}{inp("license_number", "License number")}{inp("license_expiry", "License expires", "date")}
          {inp("vet_clinic", "Vet clinic")}{inp("vet_phone", "Vet phone", "tel")}
        </div>
        <Field label="Quirks & notes"><textarea className="q-input min-h-[88px] w-full" value={f.quirks} onChange={(e) => set("quirks", e.target.value)} data-testid="pet-form-quirks" /></Field>
        <Field label="Photo (optional)"><input type="file" accept="image/jpeg,image/png,image/heic,.jpg,.jpeg,.png,.heic" className="q-input min-h-[44px] w-full" onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="pet-form-photo" /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy || !f.name.trim()} onClick={save} data-testid="pet-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </FormDialog>
  );
}

export function MealDialog({ base, pet, meal, onClose, onSaved }) {
  const [f, , inp] = useForm({ time: meal?.time || "07:30", food: meal?.food || "", amount: meal?.amount || "" });
  const save = () => run(meal ? api.put(`${base}/${pet.id}/meals/${meal.id}`, f) : api.post(`${base}/${pet.id}/meals`, f), onSaved);
  return (
    <FormDialog open onClose={onClose} title={meal ? "Edit meal" : "Add a meal"} testid="meal-dialog">
      <div className="space-y-3">{inp("time", "Time", "time")}{inp("food", "Food")}{inp("amount", "Amount", "text", "2 cups")}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={save} data-testid="meal-save">Save</button></div>
    </FormDialog>
  );
}

export function MedDialog({ base, pet, med, onClose, onSaved }) {
  const s = med?.schedule;
  const [f, set, inp] = useForm({ name: med?.name || "", dose: med?.dose || "", type: s?.type || "every", times: (s?.times || ["08:00"]).join(", "), n: s?.n || 1, unit: s?.unit || "month", start: s?.start || "" });
  const save = () => {
    const body = { ...f, n: Number(f.n), times: f.times.split(",").map((x) => x.trim()).filter(Boolean), start: f.start || null };
    run(med ? api.put(`${base}/${pet.id}/meds/${med.id}`, body) : api.post(`${base}/${pet.id}/meds`, body), onSaved);
  };
  return (
    <FormDialog open onClose={onClose} title={med ? `Edit ${med.name}` : "Add a medication"} testid="med-dialog">
      <div className="space-y-3">
        {inp("name", "Name")}{inp("dose", "Dose", "text", "1 chew")}
        <Field label="Schedule"><select className="q-input min-h-[44px] w-full" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="med-type"><option value="every">Every N days/weeks/months</option><option value="daily">Daily at set times</option></select></Field>
        {f.type === "daily" ? inp("times", "Times (24h, comma separated)", "text", "08:00, 20:00") : (
          <div className="grid grid-cols-3 gap-3">{inp("n", "Every", "number")}
            <Field label="Unit"><select className="q-input min-h-[44px] w-full" value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="med-unit">{["day", "week", "month"].map((u) => <option key={u}>{u}</option>)}</select></Field>
            {inp("start", "Starting", "date")}</div>)}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={save} data-testid="med-save">Save</button>
      </div>
    </FormDialog>
  );
}

export function VaccineDialog({ base, pet, vac, onClose, onSaved }) {
  const [f, , inp] = useForm({ name: vac?.name || "", given: vac?.given || "", next_due: vac?.next_due || "" });
  const save = () => { const body = { ...f, given: f.given || null, next_due: f.next_due || null }; run(vac ? api.put(`${base}/${pet.id}/vaccines/${vac.id}`, body) : api.post(`${base}/${pet.id}/vaccines`, body), onSaved); };
  return (
    <FormDialog open onClose={onClose} title={vac ? `Edit ${vac.name}` : "Add a vaccine"} description="The next due date goes on the Calendar, with reminders 14 and 3 days before." testid="vaccine-dialog">
      <div className="space-y-3">{inp("name", "Vaccine", "text", "Rabies")}<div className="grid grid-cols-2 gap-3">{inp("given", "Given", "date")}{inp("next_due", "Next due", "date")}</div>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={save} data-testid="vaccine-save">Save</button></div>
    </FormDialog>
  );
}

export function VisitDialog({ base, pet, visit, today, onClose, onSaved }) {
  const [f, , inp] = useForm({ date: visit?.date || today, clinic: visit?.clinic || pet.vet_clinic || "", reason: visit?.reason || "", notes: visit?.notes || "", weight: visit?.weight || "", cost: visit?.cost || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (file && checkFile(file)) { toast.error(checkFile(file)); return; }
    setBusy(true);
    try {
      const receipt_upload_id = file ? await uploadFile(base, file) : null;
      const body = { ...f, cost: f.cost === "" ? null : Number(f.cost), receipt_upload_id };
      await run(visit ? api.put(`${base}/${pet.id}/visits/${visit.id}`, body) : api.post(`${base}/${pet.id}/visits`, body), onSaved, "Saved. Spending is updated.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={visit ? "Edit vet visit" : "Log a vet visit"} description="A cost goes into Spending once as Pets. The receipt is filed in Documents → Pets." testid="visit-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">{inp("date", "Date", "date")}{inp("clinic", "Clinic")}{inp("weight", "Weight", "text", "68 lb")}{inp("cost", "Cost ($)", "number")}</div>
        {inp("reason", "Reason")}{inp("notes", "Notes")}
        <Field label="Receipt (optional)"><input type="file" accept={ACCEPT} className="q-input min-h-[44px] w-full" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="visit-receipt" /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="visit-save">{busy ? "Saving…" : "Save"}</button>
      </div>
    </FormDialog>
  );
}
